import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Flex, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { type ReactNode, useMemo } from "react";

import { baseLabelClassname } from "./assessment-question-field-common";

interface AssessmentQuestionFieldBaseLabelProps {
  generateButton?: ReactNode;
  helpText: string;
  helpLink?: string;
  labelText: string;
  withRequiredAsterisk?: boolean;
}

export const AssessmentQuestionFieldBaseLabel = ({
  generateButton = null,
  helpText,
  helpLink,
  labelText,
  withRequiredAsterisk = true,
}: AssessmentQuestionFieldBaseLabelProps) => {
  const [helpTooltipOpen, { close: closeHelpTooltip, open: openHelpTooltip }] =
    useDisclosure();
  const icon = useMemo(
    () => (
      <FontAwesomeIcon className="ml-1 text-uw-purple-3" icon={faInfoCircle} />
    ),
    [],
  );

  return (
    <Flex
      align="center"
      className="w-full"
      direction="row"
      justify="space-between"
    >
      <Flex>
        <Text className={baseLabelClassname}>
          {labelText}
          {withRequiredAsterisk && "*"}
        </Text>
        <Tooltip
          className="py-3 text-center"
          multiline
          label={helpText}
          // The `helpTooltipOpen` var is only used to open the tooltip on link
          // focus, outside of that situation we fall back to default behavior
          // (by passing `undefined`) rather than leaving continuing to control
          // the component manually
          opened={helpTooltipOpen || undefined}
        >
          {helpLink ? (
            <a
              aria-label={helpText}
              className="py-0"
              href={helpLink}
              onBlur={closeHelpTooltip}
              onClick={(e) => e.stopPropagation()}
              onFocus={openHelpTooltip}
              rel="noopener noreferrer"
              target="_blank"
            >
              {icon}
            </a>
          ) : (
            <span
              aria-label={`Help: ${helpText}`}
              className="py-0"
              role="button"
              tabIndex={0}
              onBlur={closeHelpTooltip}
              onFocus={openHelpTooltip}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                }
              }}
            >
              {icon}
            </span>
          )}
        </Tooltip>
      </Flex>
      {generateButton}
    </Flex>
  );
};
